/* eslint-disable no-param-reassign */
import { prefersReducedMotion } from './motion-utils'

/**
 * v-countup — animate a number from 0 up to a target the first time it scrolls
 * into view (the Play stats count-up from the mockups). Eased with easeOutCubic
 * over `duration` ms, then writes the formatted value into the element's text.
 *
 * Usage:
 *   <span v-countup="12000">0</span>
 *   <span v-countup="{ to: 4.9, decimals: 1, suffix: '★' }">0</span>
 *   <span v-countup="{ to: 98, suffix: '%', duration: 1200 }">0</span>
 *
 * Binding value: a Number target, or an object:
 *   to       — target number (required)
 *   decimals — fraction digits (default 0; integers get thousands separators)
 *   prefix   — string prepended (default '')
 *   suffix   — string appended (default '')
 *   duration — ms (default 1500, from the mockup)
 *   locale   — Intl locale for integer grouping (default browser default)
 *
 * When the user prefers reduced motion the final value is written immediately.
 *
 * VUE3-MIGRATION: rename `inserted`→`mounted`, `unbind`→`unmounted`. The RAF
 * tween body is unchanged.
 */

const STORE = '__playCountUp__'

function configFromBinding(binding) {
  const v = binding.value
  const cfg = typeof v === 'object' && v !== null ? v : { to: Number(v) }
  return {
    to: Number(cfg.to) || 0,
    decimals: Number(cfg.decimals) || 0,
    prefix: cfg.prefix || '',
    suffix: cfg.suffix || '',
    duration: Number(cfg.duration) || 1500,
    locale: cfg.locale || undefined,
  }
}

function format(value, cfg) {
  const body = cfg.decimals
    ? value.toFixed(cfg.decimals)
    : Math.floor(value).toLocaleString(cfg.locale)
  return `${cfg.prefix}${body}${cfg.suffix}`
}

function run(el, cfg) {
  let start = null
  const tick = now => {
    if (start === null) start = now
    const p = Math.min((now - start) / cfg.duration, 1)
    const eased = 1 - (1 - p) ** 3
    el.textContent = format(eased * cfg.to, cfg)
    if (p < 1) el[STORE].raf = requestAnimationFrame(tick)
  }
  el[STORE].raf = requestAnimationFrame(tick)
}

export const countup = {
  inserted(el, binding) {
    const cfg = configFromBinding(binding)
    el[STORE] = { cfg, raf: null, io: null }

    if (prefersReducedMotion()) {
      el.textContent = format(cfg.to, cfg)
      return
    }

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        run(el, el[STORE].cfg)
        io.unobserve(entry.target)
      })
    }, { threshold: 0.2 })

    io.observe(el)
    el[STORE].io = io
  },

  componentUpdated(el, binding) {
    if (!el[STORE]) return
    el[STORE].cfg = configFromBinding(binding)
  },

  unbind(el) {
    const state = el[STORE]
    if (!state) return
    if (state.raf) cancelAnimationFrame(state.raf)
    if (state.io) state.io.disconnect()
    delete el[STORE]
  },
}

export default countup
